import { Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { distinctUntilChanged, startWith, tap } from 'rxjs/operators';
import { SurveyModule } from 'survey-angular-ui';
import { ITheme, Model } from 'survey-core';
import { PlainLightPanelless } from 'survey-core/themes';

@Component({
    selector: 'app-survey',
    templateUrl: './survey.component.html',
    styleUrls: ['./survey.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [SurveyModule],
})
export class SurveyComponent {
    private readonly translate = inject(TranslateService);

    @Input() public theme: ITheme = PlainLightPanelless;

    private _survey?: Model;
    public get survey(): Model {
        return this._survey;
    }

    @Input()
    public set survey(surveyModel: Model) {
        surveyModel.applyTheme(this.theme);

        surveyModel.locale = this.toSurveyLocale(this.translate.currentLang);

        this.reEvaluateReadonlyExpressions(surveyModel);

        this._survey = surveyModel;
    }

    @Input()
    public compact = false;

    public constructor() {
        const translate = this.translate;

        translate.onLangChange
            .pipe(
                startWith({ lang: translate.currentLang }),
                distinctUntilChanged(isEqual),
                tap(({ lang }) => {
                    if (this._survey) this._survey.locale = this.toSurveyLocale(lang);
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }

    private toSurveyLocale(lang: string): string {
        return lang === this.translate.defaultLang ? 'default' : lang;
    }

    /**
     * If this survey is readonly, but contains an expression,
     * this survey needs to be re-evaluated to display expression results.
     * @param surveyModel
     * @private
     */
    private reEvaluateReadonlyExpressions(surveyModel: Model): void {
        if (
            surveyModel.readOnly &&
            surveyModel.getAllQuestions(false, undefined, true).some((q) => q.getType() === 'expression')
        ) {
            surveyModel.readOnly = false;
            surveyModel.mergeData({});
            surveyModel.readOnly = true;
        }

        this._survey = surveyModel;
    }
}
